import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { fetchPODetails } from "../../Services/PoException";
import {
  TextField,
  Button,
  Grid,
  Box,
  Typography,
  MenuItem,
} from "@mui/material";
import ServiceLevelService from "../../Services/ServiceLevelService";
import SCACService from "../../Services/SCACService";
import {
  statusUpdate,
  deleteException,
  updatePoException,
} from "../../Services/ExceptionPo";
import "react-toastify/dist/ReactToastify.css";
import dayjs from "dayjs";
import loaderGif from "../../Assests/loader.gif";
import "react-notifications/lib/notifications.css";
import {
  NotificationContainer,
  NotificationManager,
} from "react-notifications";
import storeService from "../../Services/Store";

const ExceptionPoDetail = () => {
  const navigate = useNavigate();
  const { poExceptionId } = useParams(); // Fetch the unique poExceptionId from URL params
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCarrier, setSelectedCarrier] = useState("");
  const [approverName, setApproverName] = useState("");
  const [storeOptions, setStoreOptions] = useState([]);
  const [isLoadingStores, setIsLoadingStores] = useState(false);
  const [storeError, setStoreError] = useState(null);
  const [errors, setErrors] = useState({});
  const [isLoadingScac, setIsLoadingScac] = useState(false);
  const [scacError, setScacError] = useState(null);
  const [initialPODetails, setInitialPODetails] = useState(null);
  const [pageLoading, setPageLoading] = useState(false);
  const [poDetails, setPODetails] = useState({
    store_list: "", // Initialize as empty string (or adjust if you expect an array)
  });

  // Service level dropdown state
  const [serviceLevelOptions, setServiceLevelOptions] = useState([]);
  const [scacOptions, setScacOptions] = useState([]);
  const [isLoadingServiceLevels, setIsLoadingServiceLevels] = useState(false);
  const [serviceLevelError, setServiceLevelError] = useState(null);
  const freightPayerOptions = [
    { id: 1, name: "Buyer" },
    { id: 2, name: "Vendor" },
    { id: 3, name: "Store" },
  ];
  const exceptionRoutingOptions = [
    { id: 1, name: "Customer Request" },
    { id: 2, name: "Personal Appearance" },
    { id: 3, name: "Store Event" },
    { id: 4, name: "Trunck Show" },
    { id: 5, name: "Vendor Delayed Shipment" },
    { id: 6, name: "Catalog Drop" },
    { id: 7, name: "Out Of Stock" },
  ];
  const billToAccountOptions = [
    { id: 1, name: "Fedex Act 1886 23 204" },
    { id: 2, name: "Freight Collect" },
    { id: 3, name: "Vendor Pays Freight" },
    { id: 4, name: "Trunck Show" },
  ];

  // Fetch store data
  useEffect(() => {
    const fetchStoreData = async () => {
      setIsLoadingStores(true);
      setStoreError(null);
      try {
        const response = await storeService.fetchStore(1, 99);
        console.log("Raw store data:", response.data);
        // Filter stores with exception_allowed set to 1
        const filteredStores = response.data.filter(
          (store) => store.exceptions_allowed === 1
        );
        console.log("Stores with exceptions_allowed set to 1:", filteredStores);
        // Set the store options for the dropdown
        setStoreOptions(
          filteredStores.map((store) => ({
            number: store.store_number,
            id: store.store_id,
            name: `${store.store_number} - ${store.name || "Unknown"}`,
            units: store.units,
          }))
        );
        
      } catch (err) {
        console.error("Error fetching store data:", err);
        setStoreError(err.message);
      } finally {
        setIsLoadingStores(false);
      }
    };

    fetchStoreData();
  }, []);

  // Fetch SCAC data
  useEffect(() => {
    const fetchScacData = async () => {
      setIsLoadingScac(true);
      setScacError(null);
      try {
        const response = await SCACService.fetchSCACs(1, 100);
        if (response) {
          const { data } = response;
          setScacOptions(
            data.map((scac) => ({
              id: scac.scac_id,
              name: `${scac.scac_name}`,
            }))
          );
        }
      } catch (err) {
        console.error("Error fetching SCAC data:", err);
        setScacError(err.message);
      } finally {
        setIsLoadingScac(false);
      }
    };

    fetchScacData();
  }, []);

  // Fetch service level data
  useEffect(() => {
    const fetchServiceLevelData = async () => {
      setIsLoadingServiceLevels(true);
      setServiceLevelError(null);
      try {
        const response = await ServiceLevelService.fetchServiceLevel(
          1,
          100,
          "service_level",
          "asc"
        );
        const { data } = response;
        setServiceLevelOptions(
          data.map((level) => ({
            id: level.service_level_id,
            name: `${level.service_level}`,
          }))
        );
      } catch (err) {
        console.error("Error fetching service level data:", err);
        setServiceLevelError(err.message);
      } finally {
        setIsLoadingServiceLevels(false);
      }
    };

    fetchServiceLevelData();
  }, []);

  // Fetch PO details
  useEffect(() => {
    console.log("poExceptionId:", poExceptionId);
    if (poExceptionId) {
      const fetchPODetail = async () => {
        setPageLoading(true);
        try {
          console.log("Fetching PO details with poExceptionId:", poExceptionId);
          const response = await fetchPODetails(poExceptionId);
          console.log("API Response:", response);
          setPODetails(response.data[0]);
          setInitialPODetails(response.data[0]);
          setLoading(false);
          if (response.data[0].scac_id) {
            setSelectedCarrier(response.data[0].scac_id);
          }
          if (response.data[0].approvers_name) {
            setApproverName(response.data[0].approvers_name);
          }
        } catch (error) {
          console.error("Failed to fetch PO details:", error);
          setLoading(false);
        } finally {
          setPageLoading(false);
        }
      };

      fetchPODetail();
    }
  }, [poExceptionId]);

  if (error) return <Typography variant="h6">{error}</Typography>;

  // Compare current form data with initial data
  const hasDataChanged = () => {
    if (!initialPODetails) return false;
    return JSON.stringify(poDetails) !== JSON.stringify(initialPODetails);
  };

  // Handle deletion
  const handleDelete = async (po_exception_id) => {
    if (!window.confirm("Are you sure you want to delete this exception?")) {
      return;
    }
    try {
      NotificationManager.removeAll();
      setPageLoading(true);
      const response = await deleteException(po_exception_id);
      if (response?.message) {
        console.log("Delete response:", response.message);
        NotificationManager.success("Deleted successfully", "Success", 1000);
      } else {
        NotificationManager.error(
          "Record not deleted, Please try again later.",
          "Error",
          1000
        );
      }
      setTimeout(() => {
        navigate("/dashboard/exceptions");
      }, 1000);
    } catch (error) {
      console.error("Error in handleDelete:", error);
    } finally {
      setPageLoading(false);
    }
  };

  // Save/Update function (called by the Save button)
  const handleUpdate = async (po_exception_id) => {
    setLoading(true);
    try {
      NotificationManager.removeAll();
      setPageLoading(true);
      const payload = {
        po_number: poDetails.po_number || "",
        vendor_name: poDetails.vendor_name || "",
        units: poDetails.units || 0,
        department_number: poDetails.department_number || 0,
        store_list: poDetails.store_list || "",
        freight_payer_id: poDetails.freight_payer_id || 0,
        bill_account_number: poDetails.bill_account_number || "",
        service_level_id: poDetails.service_level_id || 0,
        expense_number: poDetails.expense_number || "",
        requester_name: poDetails.requester_name || "",
        requester_phone: poDetails.requester_phone || "",
        requester_email: poDetails.requester_email || "",
        exception_routing_reason: poDetails.exception_routing_reason || "",
        vendor_ship_date: poDetails.vendor_ship_date || "",
        date_needed_store: poDetails.date_needed_store || "",
        comment: poDetails.comment || "",
        status: poDetails.status || "pending",
        client_name: poDetails.client_name || "",
      };

      console.log("Payload:", payload);
      const result = await updatePoException(po_exception_id, payload);
      if (result?.message) {
        NotificationManager.success(
          "Data updated successfully.",
          "Success",
          1000
        );
      }
      setTimeout(() => {
        navigate("/dashboard/exceptions");
      }, 1000);
    } catch (error) {
      console.error("Update failed:", error);
    } finally {
      setPageLoading(false);
      setLoading(false);
    }
  };

  // Approve function: update form first if needed, then call statusUpdate with Approved status
  const handleApprove = async (po_exception_id) => {
    try {
      setPageLoading(true);
      NotificationManager.removeAll();

      // If form data changed, update it first
      if (hasDataChanged()) {
        const payload = {
          po_number: poDetails.po_number || "",
          vendor_name: poDetails.vendor_name || "",
          units: poDetails.units || 0,
          department_number: poDetails.department_number || 0,
          store_list: poDetails.store_list || "",
          freight_payer_id: poDetails.freight_payer_id || 0,
          bill_account_number: poDetails.bill_account_number || "",
          service_level_id: poDetails.service_level_id || 0,
          expense_number: poDetails.expense_number || "",
          requester_name: poDetails.requester_name || "",
          requester_phone: poDetails.requester_phone || "",
          requester_email: poDetails.requester_email || "",
          exception_routing_reason: poDetails.exception_routing_reason || "",
          vendor_ship_date: poDetails.vendor_ship_date || "",
          date_needed_store: poDetails.date_needed_store || "",
          comment: poDetails.comment || "",
          status: poDetails.status || "pending",
          client_name: poDetails.client_name || "",
        };

        const updateResult = await updatePoException(po_exception_id, payload);
        if (!updateResult?.message) {
          NotificationManager.error(
            "Failed to update form data",
            "Error",
            1000
          );
          return;
        }
      }

      // Now update the status to Approved
      const payloadApprove = {
        status: "Approved",
        carrier_id: selectedCarrier,
        approvers_name: approverName,
      };

      const resultApprove = await statusUpdate(po_exception_id, payloadApprove);
      if (resultApprove?.message) {
        NotificationManager.success(
          "Exception Approved Successfully",
          "Success",
          1000
        );
        setTimeout(() => {
          navigate(-1);
        }, 1000);
      } else {
        NotificationManager.error(
          "Failed to approve exception. Please try again later.",
          "Error",
          1000
        );
      }
    } catch (error) {
      console.error("Error approving exception:", error);
    } finally {
      setPageLoading(false);
    }
  };

  // Decline function: simply update status to Declined
// Decline function: update form first if needed, then call statusUpdate with Declined status
const handleDeny = async (po_exception_id) => {
  try {
    setPageLoading(true);
    NotificationManager.removeAll();

    // If form data changed, update it first
    if (hasDataChanged()) {
      const payload = {
        po_number: poDetails.po_number || "",
        vendor_name: poDetails.vendor_name || "",
        units: poDetails.units || 0,
        department_number: poDetails.department_number || 0,
        store_list: poDetails.store_list || "",
        freight_payer_id: poDetails.freight_payer_id || 0,
        bill_account_number: poDetails.bill_account_number || "",
        service_level_id: poDetails.service_level_id || 0,
        expense_number: poDetails.expense_number || "",
        requester_name: poDetails.requester_name || "",
        requester_phone: poDetails.requester_phone || "",
        requester_email: poDetails.requester_email || "",
        exception_routing_reason: poDetails.exception_routing_reason || "",
        vendor_ship_date: poDetails.vendor_ship_date || "",
        date_needed_store: poDetails.date_needed_store || "",
        comment: poDetails.comment || "",
        status: poDetails.status || "Pending",
        client_name: poDetails.client_name || "",
      };

      const updateResult = await updatePoException(po_exception_id, payload);
      if (!updateResult?.message) {
        NotificationManager.error(
          "Failed to update form data",
          "Error",
          1000
        );
        return;
      }
    }

    // Now update the status to Declined
    const payload = {
      status: "Declined",
    };
    
    const result = await statusUpdate(po_exception_id, payload);
    if (result?.message) {
      NotificationManager.success(
        "Exception Declined",
        "Success",
        1000
      );
      setTimeout(() => {
        navigate("/dashboard/exceptions");
      }, 1000);
    } else {
      NotificationManager.error(
        "Failed to decline exception. Please try again later.",
        "Error",
        1000
      );
    }
  } catch (error) {
    console.error("Error in handleDeny:", error);
  } finally {
    setPageLoading(false);
  }
};

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setPODetails((prevDetails) => ({
      ...prevDetails,
      [name]: name === "store_list" ? String(value) : value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log("Form submitted with updated data:", poDetails);
  };

  const handleCancel = () => {
    navigate("/dashboard/exceptions");
  };

  return (
    <div>
      {(pageLoading || loading) && (
        <Box
          position="fixed"
          top={0}
          left={0}
          right={0}
          bottom={0}
          display="flex"
          justifyContent="center"
          alignItems="center"
          bgcolor="rgba(255, 255, 255, 0.8)"
          zIndex={9999}
          width="100%"
          height="120%"
        >
          <Box display="flex">
            <img src={loaderGif} alt="Loading..." width={50} height={50} />
          </Box>
        </Box>
      )}
      <Box
        sx={{
          padding: "30px",
          maxWidth: "1000px",
          margin: "100px auto",
          marginBottom: "20px",
          border: "2px solid",
          borderRadius: "5px",
          backgroundColor: "#f5f5f5",
        }}
      >
        <NotificationContainer />

        <Typography variant="h4" gutterBottom align="center" fontWeight="bold">
          PO Exceptions Update Form
        </Typography>

        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="PO Number"
                name="po_number"
                value={poDetails?.po_number || ""}
                onChange={handleChange}
                variant="outlined"
                margin="dense"
              />
            </Grid>

            {/* <Grid item xs={12}>
              <TextField
                fullWidth
                label="Supplier Site ID"
                name="supplier_site_id"
                value={poDetails?.supplier_site_id || ""}
                onChange={handleChange}
                variant="outlined"
                margin="dense"
              />
            </Grid> */}

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Vendor Name"
                name="vendor_name"
                value={poDetails?.vendor_name || ""}
                onChange={handleChange}
                variant="outlined"
                margin="dense"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Client Name"
                name="client_name"
                value={poDetails?.client_name || ""}
                onChange={handleChange}
                variant="outlined"
                margin="dense"
                disabled
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                labelId="store-list-label"
                margin="dense"
                required
                fullWidth
                name="store_list"
                id="store_list"
                select
                label="Store Number"
                variant="outlined"
                value={poDetails.store_list || ""}
                onChange={handleChange}
                error={!!errors.store_list}
                helperText={errors.store_list}
              >
                {storeOptions.map((option) => (
                  <MenuItem key={option.number} value={option.number}>
                    {option.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <TextField
                margin="dense"
                required
                fullWidth
                name="freight_payer_id"
                id="freight_payer_id"
                select
                label="Freight Payer"
                variant="outlined"
                value={poDetails.freight_payer_id || ""}
                onChange={handleChange}
                error={!!errors.freight_payer_id}
                helperText={errors.freight_payer_id}
              >
                {freightPayerOptions.map((option) => (
                  <MenuItem key={option.id} value={option.name}>
                    {option.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <TextField
                margin="dense"
                required
                fullWidth
                name="bill_account_number"
                id="bill_account_number"
                select
                label="Bill to Account Number"
                variant="outlined"
                value={poDetails.bill_account_number || ""}
                onChange={handleChange}
                error={!!errors.bill_account_number}
                helperText={errors.bill_account_number}
              >
                {billToAccountOptions.map((option) => (
                  <MenuItem key={option.id} value={option.name}>
                    {option.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Expense Number"
                name="expense_number"
                value={poDetails?.expense_number || ""}
                onChange={handleChange}
                variant="outlined"
                margin="dense"
              />
            </Grid> 
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Units"
                name="units" 
                value={poDetails?.units || 0}
                onChange={handleChange}
                variant="outlined"
                margin="dense"
                disabled
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                margin="dense"
                required
                fullWidth
                name="service_level_id"
                id="service_level_id"
                select
                label="Service Level"
                variant="outlined"
                value={poDetails.service_level_id || ""}
                onChange={handleChange}
                error={!!errors.service_level_id}
                helperText={errors.service_level_id}
                disabled={isLoadingServiceLevels}
              >
                {serviceLevelOptions.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Requester Name"
                name="requester_name"
                value={poDetails?.requester_name || ""}
                onChange={handleChange}
                variant="outlined"
                margin="dense"
                disabled={!!poDetails.requester_name}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Requester Phone"
                name="requester_phone"
                value={poDetails?.requester_phone || ""}
                onChange={handleChange}
                variant="outlined"
                margin="dense"
                disabled={!!poDetails.requester_phone}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Requester Email"
                name="requester_email"
                value={poDetails?.requester_email || ""}
                onChange={handleChange}
                variant="outlined"
                margin="dense"
              />
            </Grid>

            {/* Updated Comment Field: Now always editable and multiline */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Reason for Approve/Decline"
                name="comment"
                value={poDetails?.comment || ""}
                onChange={handleChange}
                variant="outlined"
                margin="dense"
                multiline
                rows={3}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Authorization Token"
                value={`${(poDetails?.store_list || "").trim()}${(poDetails?.po_number || "").trim()}`}
                disabled
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                margin="dense"
                required
                fullWidth
                name="exception_routing_reason"
                id="exception_routing_reason"
                select
                label="Reason for Exception Routing"
                variant="outlined"
                value={poDetails.exception_routing_reason || ""}
                onChange={handleChange}
                error={!!errors.exception_routing_reason}
                helperText={
                  errors.exception_routing_reason ||
                  (serviceLevelError && "Error loading service levels")
                }
                disabled={isLoadingServiceLevels}
              >
                {exceptionRoutingOptions.map((option) => (
                  <MenuItem key={option.id} value={option.name}>
                    {option.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Approved Date"
                name="approved_date"
                id="approved_date"
                type="date"
                value={
                  poDetails?.approved_date
                    ? dayjs(poDetails.approved_date).format("YYYY-MM-DD")
                    : ""
                }
                onChange={handleChange}
                variant="outlined"
                margin="dense"
                InputLabelProps={{ shrink: true }}
                disabled
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Date Needed in Store"
                name="date_needed_store"
                type="date"
                value={
                  poDetails?.date_needed_store
                    ? dayjs(poDetails.date_needed_store).format("YYYY-MM-DD")
                    : ""
                }
                onChange={handleChange}
                variant="outlined"
                margin="dense"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Vendor Ready to Ship Date"
                name="vendor_ship_date"
                type="date"
                value={
                  poDetails?.vendor_ship_date
                    ? dayjs(poDetails.vendor_ship_date).format("YYYY-MM-DD")
                    : ""
                }
                onChange={handleChange}
                variant="outlined"
                margin="dense"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </form>
      </Box>

      {/* Approval Details Section */}
      <Box
        sx={{
          padding: "30px",
          maxWidth: 800,
          border: "2px solid",
          marginTop: "20px",
          marginLeft: "auto",
          marginRight: "auto",
          backgroundColor: "#f5f5f5",
        }}
      >
        <form>
          <Box sx={{ display: "flex", marginBottom: 2 }}>
            <Typography
              variant="body1"
              sx={{ minWidth: "150px", fontWeight: "bold", marginRight: "8px" }}
            >
              Carrier's Name:
            </Typography>
            <TextField
              fullWidth
              select
              name="carrier_name"
              value={selectedCarrier}
              onChange={(e) => setSelectedCarrier(e.target.value)}
              variant="outlined"
              margin="dense"
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {scacOptions.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.name}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Box sx={{ display: "flex", marginBottom: 2 }}>
            <Typography
              variant="body1"
              sx={{ minWidth: "150px", fontWeight: "bold", marginRight: "8px" }}
            >
              Approver's Name:
            </Typography>
            <TextField
              fullWidth
              name="approver_name"
              value={approverName}
              onChange={(e) => setApproverName(e.target.value)}
              variant="outlined"
              margin="dense"
              placeholder="Enter approver's name"
            />
          </Box>
        </form>
      </Box>

      {/* Action Buttons */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          gap: "10px",
          marginTop: "20px",
          marginBottom: "120px",
        }}
      >
        <Button
          type="submit"
          variant="contained"
          color="primary"
          onClick={() => handleUpdate(poDetails.po_exception_id)}
          disabled={!hasDataChanged()}
          sx={{ padding: "8px" }}
        >
          Save
        </Button>
        <Button
          type="button"
          variant="contained"
          color="success"
          onClick={() => handleApprove(poDetails.po_exception_id)}
          sx={{ padding: "8px" }}
          disabled={!selectedCarrier || !approverName.trim()}
        >
          Approve
        </Button>
        <Button
          type="button"
          variant="contained"
          color="error"
          onClick={() => handleDeny(poDetails.po_exception_id)}
          sx={{ padding: "8px" }}
        >
          Decline
        </Button>
        <Button
          type="button"
          variant="contained"
          onClick={handleCancel}
          sx={{ padding: "8px", backgroundColor: "#9e9e9e", color: "white" }}
        >
          Cancel
        </Button>
        <Button
          type="button"
          variant="contained"
          color="error"
          onClick={() => handleDelete(poDetails.po_exception_id)}
          sx={{ padding: "8px" }}
        >
          Delete
        </Button>
      </Box>
    </div>
  );
};

export default ExceptionPoDetail;
